import { useCallback, useState } from "react";
import Radio from "../entities/Radio";
import { ITeamDeviceDB } from "../interfaces/Team";

interface MicrophoneData {
  name: string;
  deviceInfo: MediaDeviceInfo;
}

interface UseAudioConfirmationResult {
  showModal: boolean;
  availableMicrophones: MicrophoneData[];
  checkAudioBeforeStreaming: () => Promise<boolean>;
  handleEnableMicrophones: () => void;
  handleContinueWithoutAudio: () => void;
  handleCancel: () => void;
}

export const useAudioConfirmation = (): UseAudioConfirmationResult => {
  const [showModal, setShowModal] = useState(false);
  const [availableMicrophones, setAvailableMicrophones] = useState<
    MicrophoneData[]
  >([]);
  const [resolvePromise, setResolvePromise] = useState<
    ((value: boolean) => void) | null
  >(null);

  const checkAudioBeforeStreaming = useCallback((): Promise<boolean> => {
    return new Promise((resolve) => {
      const radio = new Radio();

      const hasActiveMics = radio.Microphones.hasActiveMicrophones();

      if (hasActiveMics) {
        resolve(true);
        return;
      }

      const allMicrophones = radio.Microphones.getAll().map(
        (mic: ITeamDeviceDB) => ({
          name: mic.name,
          deviceInfo: mic.deviceInfo as unknown as MediaDeviceInfo,
        }),
      );

      setAvailableMicrophones(allMicrophones);
      setShowModal(true);
      setResolvePromise(() => resolve);
    });
  }, []);

  const handleEnableMicrophones = useCallback(() => {
    const radio = new Radio();

    if (availableMicrophones.length > 0) {
      const firstMic = availableMicrophones[0];
      radio.Microphones.play(firstMic.deviceInfo.deviceId);

      console.log(`🎤 Microphone automatically enabled: ${firstMic.name}`);
    }

    setShowModal(false);
    if (resolvePromise) {
      resolvePromise(true);
      setResolvePromise(null);
    }
  }, [availableMicrophones, resolvePromise]);

  const handleContinueWithoutAudio = useCallback(() => {
    console.log("⚠️ User continued streaming without audio");
    setShowModal(false);
    if (resolvePromise) {
      resolvePromise(true);
      setResolvePromise(null);
    }
  }, [resolvePromise]);

  const handleCancel = useCallback(() => {
    console.log("❌ User cancelled streaming");
    setShowModal(false);
    if (resolvePromise) {
      resolvePromise(false);
      setResolvePromise(null);
    }
  }, [resolvePromise]);

  return {
    showModal,
    availableMicrophones,
    checkAudioBeforeStreaming,
    handleEnableMicrophones,
    handleContinueWithoutAudio,
    handleCancel,
  };
};
